import Container from '@mui/material/Container'
import React, { useEffect, useState } from 'react'

import { SuperDocEditor } from '@superdoc-dev/react'
import '@superdoc-dev/react/style.css'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'

import PropTypes from 'prop-types'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'

import CircularProgress from '@mui/material/CircularProgress'

import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import useMediaQuery from '@mui/material/useMediaQuery'

import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import CodeIcon from '@mui/icons-material/Code'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'

import axios from './api'

import ParaphraseModal from './components/ParaphraseModal.tsx'


//  import schemaData from './schema.json'

//axios.defaults.baseURL = 'http://localhost:5000'
function TabPanel(props) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box
                    sx={{
                        p: { xs: 2, sm: 3 },
                        backgroundColor: '#f8fafc',
                        border: '1px solid #dbe3ee',
                        borderRadius: '10px',
                        marginTop: '12px',
                        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.05)'
                    }}
                >
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    )
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    }
}

const getDocumentText = doc =>
    doc.textBetween(0, doc.content.size, '\n', '\n')

const COLORADO_PRIVACY_LAW = 'Colorado Privacy Law'
const VIRGINIA_PRIVACY_LAW =
    'Virginia Consumer Data Protection Act (VCDPA)'
const CALIFORNIA_PRIVACY_LAW = 'California Consumer Privacy Act (CCPA)'

const lawClauseNames = {
    [COLORADO_PRIVACY_LAW]: [
        'Appeals',
        'Authentication Process Description',
        'Authorized Agent Instructions',
        'Automated Decision-Making Disclosure',
        'Categories of Personal Data Collected',
        'Changes to Policy',
        'Consumer Rights',
        'Controller Identity',
        'Opt-Out Rights and Mechanism',
        'Purpose of Collecting Personal Data',
        'Purpose of Processing',
        'Sale of Personal Data',
        'Sensitive Personal Data',
        'Sharing of Personal Data',
        'Targeted Advertising and Profiling',
        'Universal Opt-Out Mechanism (UOOM)'
    ],
    [VIRGINIA_PRIVACY_LAW]: [
        'Appeals',
        'Categories of Personal Data Collected',
        "Children's Data Processing",
        'Consumer Rights',
        'Opt-Out Rights and Mechanism',
        'Purpose of Processing',
        'Sale of Personal Data',
        'Sensitive Personal Data',
        'Sharing of Personal Data',
        'Targeted Advertising and Profiling'
    ],
    [CALIFORNIA_PRIVACY_LAW]: [
        'Authentication Process Description',
        'Authorized Agent Instructions',
        'Automated Decision-Making Disclosure',
        'Categories of Personal Data Collected',
        'Categories of Personal Information Disclosed for a Business Purpose',
        'Changes to Policy',
        "Children's Data Processing",
        'Consumer Rights',
        'Controller Identity',
        'Notice of Financial Incentive',
        'Opt-Out Preference Signals',
        'Opt-Out Rights and Mechanism',
        'Purpose of Processing',
        'Retention Disclosure',
        'Sale and Sharing of Personal Data',
        'Sensitive Personal Information'
    ],
}

const supportsExplore = contractType => contractType === COLORADO_PRIVACY_LAW
const MODEL_OPTIONS = [
    {
        value: 'haiku',
        label: 'Haiku 4.5',
        efforts: ['default', 'low', 'medium', 'high']
    },
    {
        value: 'sonnet',
        label: 'Sonnet 5',
        efforts: ['default', 'low', 'medium', 'high', 'xhigh', 'max']
    },
    {
        value: 'opus',
        label: 'Opus 5',
        efforts: ['default', 'low', 'medium', 'high', 'xhigh', 'max']
    },
    {
        value: 'gpt-sol',
        label: 'GPT-5.6 Sol',
        efforts: ['default', 'none', 'low', 'medium', 'high', 'xhigh', 'max']
    },
    {
        value: 'gpt-terra',
        label: 'GPT-5.6 Terra',
        efforts: ['default', 'none', 'low', 'medium', 'high', 'xhigh', 'max']
    },
    {
        value: 'gpt-luna',
        label: 'GPT-5.6 Luna',
        efforts: ['default', 'none', 'low', 'medium', 'high', 'xhigh', 'max']
    }
]
const EFFORT_LABELS = {
    default: 'Default',
    none: 'None',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'Extra high',
    max: 'Maximum'
}
const getModelEfforts = model =>
    MODEL_OPTIONS.find(option => option.value === model)?.efforts || ['default']
const CLAUSE_ANALYSIS_CONCURRENCY = 15
const CLAUSE_ANALYSIS_STAGGER_MS = 350
const DEFAULT_DOCUMENT_VIEWPORT_WIDTH = 816
const SUPERDOC_MODULES = { comments: false }
const FINDING_OUTCOME_STYLES = {
    satisfied: { accent: '#2e7d32' },
    'requires-revision': { accent: '#c62828' },
    'needs-review': { accent: '#a16207' },
    'not-applicable': { accent: '#64748b' },
    unknown: { accent: '#1976d2' }
}

export const getFindingOutcome = analysisText => {
    const outcomeMatch = String(analysisText || '').match(
        /\boutcome\s*:?\s*([\s\S]{0,160})/i
    )
    if (!outcomeMatch) {
        return 'unknown'
    }

    const outcome = outcomeMatch[1]
        .replace(/\\?_/g, ' ')
        .replace(/[-\u2013\u2014]/g, ' ')
        .replace(/^[^a-z]+/i, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

    if (/^(requires\s+revi\w*|outcome not satisfied|not satisfied)\b/.test(outcome)) {
        return 'requires-revision'
    }
    if (/^(needs review|outcome conditional|conditional)\b/.test(outcome)) {
        return 'needs-review'
    }
    if (/^(not applicable|outcome not applicable)\b/.test(outcome)) {
        return 'not-applicable'
    }
    if (/^(satisfied|outcome satisfied)\b/.test(outcome)) {
        return 'satisfied'
    }
    return 'unknown'
}

export const formatElapsedTime = elapsedSeconds => {
    const minutes = Math.floor(elapsedSeconds / 60)
    const seconds = elapsedSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const formatDurationMs = durationMs =>
    formatElapsedTime(Math.floor(durationMs / 1000))

export const runStaggeredTasks = async (
    items,
    task,
    concurrency = CLAUSE_ANALYSIS_CONCURRENCY,
    staggerMs = CLAUSE_ANALYSIS_STAGGER_MS
) => {
    let nextIndex = 0
    const workerCount = Math.min(Math.max(concurrency, 1), items.length)
    const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

    const runWorker = async workerIndex => {
        if (workerIndex > 0 && staggerMs > 0) {
            await wait(workerIndex * staggerMs)
        }

        while (nextIndex < items.length) {
            const itemIndex = nextIndex++
            await task(items[itemIndex], itemIndex)
            if (nextIndex < items.length && staggerMs > 0) {
                await wait(staggerMs)
            }
        }
    }

    await Promise.all(
        Array.from({ length: workerCount }, (_, workerIndex) =>
            runWorker(workerIndex)
        )
    )
}

const getDocPositionFromTextOffset = (doc, offset) => {
    const maxPos = doc?.content?.size || 0

    if (!maxPos) {
        return 0
    }

    const safeOffset = Math.max(0, offset || 0)
    const fullText = doc.textBetween(0, maxPos, '\n', '\n')
    const clampedOffset = Math.min(safeOffset, fullText.length)

    if (clampedOffset === 0) {
        return 1
    }

    if (clampedOffset >= fullText.length) {
        return maxPos
    }

    let low = 0
    let high = maxPos

    while (low < high) {
        const mid = Math.floor((low + high) / 2)
        const midText = doc.textBetween(0, mid, '\n', '\n')
        if (midText.length < clampedOffset) {
            low = mid + 1
        } else {
            high = mid
        }
    }

    return Math.max(1, Math.min(low, maxPos))
}

function App() {
    const ANALYZE_CLAUSE_RECIPE_ENABLED = true
    const isMobileViewport = useMediaQuery('(max-width:599px)')
    const debugModelsEnabled = new URLSearchParams(window.location.search).has(
        'debug'
    )
    const editorRef = React.useRef(null)
    const editorCanvasRef = React.useRef(null)
    const [superdocInstance, setSuperdocInstance] = React.useState(null)
    const [visibleEditor, setVisibleEditor] = React.useState(null)
    const [isEditorReady, setIsEditorReady] = React.useState(false)
    const [documentViewportWidth, setDocumentViewportWidth] = React.useState(
        DEFAULT_DOCUMENT_VIEWPORT_WIDTH
    )
    const [analysisPending, setAnalysisPending] = React.useState(null)
    const [documentFile, setDocumentFile] = React.useState(null)
    const [toolbarVersion, setToolbarVersion] = React.useState(0)
    const [isLoading, setIsLoading] = React.useState(false)
    const [analysisStartedAt, setAnalysisStartedAt] = React.useState(null)
    const [analysisElapsedSeconds, setAnalysisElapsedSeconds] = React.useState(0)
    const [identificationDurationMs, setIdentificationDurationMs] =
        React.useState(null)
    const [clauseAnalysisDurationMs, setClauseAnalysisDurationMs] =
        React.useState(null)
    const [totalAnalysisDurationMs, setTotalAnalysisDurationMs] =
        React.useState(null)
    const [identificationModel, setIdentificationModel] = React.useState('haiku')
    const [analysisModel, setAnalysisModel] = React.useState('sonnet')
    const [identificationEffort, setIdentificationEffort] =
        React.useState('default')
    const [analysisEffort, setAnalysisEffort] = React.useState('high')
    const [analysis, setAnalysis] = React.useState('')
    const [hasTabResult, setHasTabResult] = React.useState(false)
    const [comments, setComments] = React.useState([])
    const [identifiedClauses, setIdentifiedClauses] = React.useState({})
    const commentIdRef = React.useRef(1)
    const pendingScrollCommentIdRef = React.useRef(null)
    const [activeCommentId, setActiveCommentId] = React.useState(null)
    const [expandedCommentId, setExpandedCommentId] = React.useState(null)
    const [analysisPromptsByClause, setAnalysisPromptsByClause] =
        React.useState({})
    const [analysisPromptDialog, setAnalysisPromptDialog] = React.useState({
        open: false,
        commentId: null,
        clauseType: '',
        draft: '',
        loading: false,
        rerunning: false,
        error: ''
    })


    // for setting contract types
    const [contractTypes, setContractTypes] = useState([])
    const [selectedContractType, setSelectedContractType] = useState('')

    //const [analysisButtons, setAnalysisButtons] = React.useState([])
    //const [summaryButtons, setSummaryButtons] = React.useState([])
    /*
    const [
        alternativePhraseButtons,
        setAlternativePhraseButtons
    ] = React.useState([])
    */
    const [serverErrors, setServerErrors] = React.useState(0)

    //dialog
    const [openSettings, setOpenSettings] = React.useState(false)
    const [pendingContractType, setPendingContractType] = React.useState('')
    const [hasUploadedDocument, setHasUploadedDocument] = React.useState(false)

    const [buttons, setButtons] = React.useState([])
    const [steps, setSteps] = React.useState({ steps: 0, min: 0, max: 0 })

    const [paraphraseModalOpen, setParaphraseModalOpen] = useState(false)

    const [clause, setClause] = useState('original clause.')
    const [clauseObj, setClauseObj] = useState({})
    const [clauseType, setClauseType] = useState('')
    const [newFavClause, setNewFavClause] = useState('')

    const paraphraseModalHandleOpen = () => {
        setParaphraseModalOpen(true)
    }

    const paraphraseModalClose = () => {
        // reset clause
        setClause('')
        setParaphraseModalOpen(false)
    }
    const uploadInputRef = React.useRef(null)

    const handleOpenUpload = () => {
        if (uploadInputRef.current) {
            uploadInputRef.current.click()
        }
    }

    const handleUpdatedClause = updatedClause => {
        if (updatedClause !== null) {
            setClause(updatedClause)
        }
        paraphraseModalClose()
        //alert(JSON.stringify(clauseObj))
        //alert(updatedClause)

        if (updatedClause == null) {
            setNewFavClause('')
            return
        }

        if (clauseObj && clauseObj.startIndex) {
            let startIndex = clauseObj['startIndex']
            let endIndex = clauseObj['endIndex']
            console.log('replace text startindex ' + startIndex)
            console.log('replace text endIndex' + endIndex)
            replaceTextRange(startIndex, endIndex, updatedClause, {
                color: 'green',
                highlight: 'yellow'
            })
            //alert('deleting text')
        }
        // reset modal
        setNewFavClause('')
    }

    const handleOnClauseFavorabilityChange = async newFavValue => {
        // get clause type
        setIsLoading(true)
        let data = {
            contractType: selectedContractType,
            clauseType: clauseType,
            clause: clause,
            favorabilityValue: newFavValue
        }
        try {
            let res = await axios.post('/alter_favorability', data)
            let ans = res.data

            //alert(JSON.stringify(ans))
            if (ans && ans.alteredClause) {
                setNewFavClause(ans.alteredClause)
            }

            console.log('response')
            console.log(ans)
            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            handleOnClauseFavorabilityChange(newFavValue)
        }

        setIsLoading(false)
    }

    const renderButtons = (tab, contractType) => {
        // close analysis
        setAnalysis('')
        setHasTabResult(false)

        const exploreTopicButtons = [
            {
                name: 'Scope & Governance',
                topicName: 'Scope & Governance',
                clauseType: 'Scope'
            },
            {
                name: 'Personal Data Collection',
                topicName: 'Personal Data Collection',
                clauseType: 'Categories of Personal Data Collected'
            },
            {
                name: 'Sources of Personal Data',
                topicName: 'Sources of Personal Data',
                clauseType: null
            },
            {
                name: 'Use of Personal Data',
                topicName: 'Use of Personal Data',
                clauseType: 'Purpose of Collecting Personal Data'
            },
            {
                name: 'Data Sharing & Third Parties',
                topicName: 'Data Sharing & Third Parties',
                clauseType: 'Sharing of Personal Data'
            },
            {
                name: 'Advertising, Sale & Profiling',
                topicName: 'Advertising, Sale & Profiling',
                clauseType: 'Targeted Advertising and Profiling'
            },
            {
                name: 'Sensitive & Data',
                topicName: 'Sensitive & Data',
                clauseType: null
            },
            {
                name: 'Consumer Rights',
                topicName: 'Consumer Rights',
                clauseType: 'Consumer Rights'
            },
            {
                name: 'Requests, Appeals & Enforcement',
                topicName: 'Requests, Appeals & Enforcement',
                clauseType: 'Appeals'
            },
            {
                name: 'Data Governance & Protection',
                topicName: 'Data Governance & Protection',
                clauseType: null
            }
        ]

        if (tab === 'explore') {
            setButtons(exploreTopicButtons)
            return
        }

        const clauseButtons = (lawClauseNames[contractType] || []).map(clauseName => ({
            name: clauseName,
            type: clauseName
        }))

        setButtons(clauseButtons)
    }
    const handleCloseSettings = (event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return
        }

        if (!pendingContractType) {
            return
        }

        const initialTab = supportsExplore(pendingContractType) ? 0 : 1
        const tab = initialTab === 0 ? 'explore' : 'analysis'
        setSelectedContractType(pendingContractType)
        setTabValue(initialTab)
        renderButtons(tab, pendingContractType)
        setOpenSettings(false)
        if (hasUploadedDocument) {
            setAnalysisPending(pendingContractType)
        }
    }

    const getActiveEditor = () => {
        if (!superdocInstance) {
            return visibleEditor || null
        }

        const editorFromGetter =
            typeof superdocInstance.getEditor === 'function'
                ? superdocInstance.getEditor()
                : null
        const candidates = [
            visibleEditor,
            editorFromGetter,
            superdocInstance.activeEditor,
            superdocInstance.editor,
            superdocInstance.presentationEditor,
            superdocInstance.state?.documents?.[0]?.editor
        ].filter(Boolean)

        const documentEditors = superdocInstance.state?.documents
            ? superdocInstance.state.documents
                  .map(doc => doc?.editor)
                  .filter(Boolean)
            : []

        const uniqueEditors = []
        const seen = new Set()
        for (const editor of [...candidates, ...documentEditors]) {
            if (!editor || seen.has(editor)) {
                continue
            }
            seen.add(editor)
            uniqueEditors.push(editor)
        }

        const isVisibleEditor = editor => {
            const view = editor?.view
            const dom = view?.dom
            if (!dom || !dom.getBoundingClientRect) {
                return false
            }
            if (dom.closest?.('.presentation-editor__hidden-host-wrapper')) {
                return false
            }
            const rect = dom.getBoundingClientRect()
            if (!rect.width || !rect.height) {
                return false
            }
            if (dom.offsetParent === null) {
                return false
            }
            return true
        }

        const presentationVisible = uniqueEditors.find(
            editor => editor?.visibleHost instanceof HTMLElement
        )
        if (presentationVisible) {
            return presentationVisible
        }

        const visible = uniqueEditors.find(isVisibleEditor)
        return visible || uniqueEditors[0] || null
    }

    const getEditorApi = () => {
        const active = getActiveEditor()
        if (active?.commands) {
            return active
        }
        if (active?.editor?.commands) {
            return active.editor
        }
        const editor = superdocInstance?.editor
        if (editor?.commands) {
            return editor
        }
        if (editor?.editor?.commands) {
            return editor.editor
        }
        const fallback = superdocInstance?.state?.documents?.[0]?.editor
        if (fallback?.commands) {
            return fallback
        }
        if (fallback?.editor?.commands) {
            return fallback.editor
        }
        return active || editor || fallback || null
    }

    const getPresentationEditor = () => {
        const candidates = [
            superdocInstance?.presentationEditor,
            visibleEditor?.presentationEditor,
            superdocInstance?.activeEditor?.presentationEditor,
            superdocInstance?.editor?.presentationEditor,
            visibleEditor,
            superdocInstance?.activeEditor,
            superdocInstance?.editor
        ].filter(Boolean)

        return (
            candidates.find(
                editor =>
                    editor?.visibleHost instanceof HTMLElement ||
                    typeof editor?.scrollToPositionAsync === 'function' ||
                    typeof editor?.coordsAtPos === 'function'
            ) || null
        )
    }

    const getCommandApi = () => {
        const editor = getEditorApi()
        return editor?.commands || null
    }

    const isActiveFormat = (name, attrs) => {
        const editor = getEditorApi()
        const commands = editor?.commands
        if (editor?.isActive) {
            return editor.isActive(name, attrs)
        }
        if (commands?.isActive) {
            return commands.isActive(name, attrs)
        }
        return false
    }

    const runEditorCommand = (command, attrs) => {
        const editor = getEditorApi()
        if (!editor) {
            return false
        }

        if (superdocInstance?.focus) {
            superdocInstance.focus()
        }

        if (editor.chain) {
            const chain = editor.chain()
            const focusedChain = chain?.focus ? chain.focus() : chain
            const chainCommand = focusedChain?.[command]
            if (typeof chainCommand === 'function') {
                const chained =
                    attrs !== undefined ? chainCommand(attrs) : chainCommand()
                if (typeof chained?.run === 'function') {
                    return chained.run()
                }
                if (typeof focusedChain?.run === 'function') {
                    return focusedChain.run()
                }
                return true
            }
        }

        if (editor?.focus) {
            editor.focus()
        }
        if (editor?.commands?.focus) {
            editor.commands.focus()
        }
        const commandFn = editor?.commands?.[command]
        if (typeof commandFn === 'function') {
            return attrs !== undefined ? commandFn(attrs) : commandFn()
        }

        return false
    }

    const handleToolbarMouseDown = event => {
        event.preventDefault()
    }

    const focusEditor = () => {
        const editor = getEditorApi()
        if (editor?.focus) {
            editor.focus()
        }
        if (editor?.commands?.focus) {
            editor.commands.focus()
        }
    }
    const bumpToolbar = React.useCallback(() => {
        setToolbarVersion(prev => prev + 1)
    }, [])

    React.useEffect(() => {
        const activeEditor = getActiveEditor()
        const dom = activeEditor?.view?.dom
        if (!dom) {
            return undefined
        }

        const handleSelectionChange = () => {
            bumpToolbar()
        }

        dom.addEventListener('mouseup', handleSelectionChange)
        dom.addEventListener('keyup', handleSelectionChange)
        document.addEventListener('selectionchange', handleSelectionChange)

        return () => {
            dom.removeEventListener('mouseup', handleSelectionChange)
            dom.removeEventListener('keyup', handleSelectionChange)
            document.removeEventListener('selectionchange', handleSelectionChange)
        }
    }, [superdocInstance, documentFile, bumpToolbar])

    const getEditorText = () => {
        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return ''
        }
        return getDocumentText(activeEditor.state.doc)
    }

    const getEditorHtml = () => {
        if (!superdocInstance?.getHTML) {
            return ''
        }
        const htmlSections = superdocInstance.getHTML()
        if (Array.isArray(htmlSections)) {
            return htmlSections.join('\n')
        }
        return htmlSections || ''
    }

    const replaceTextRange = (startIndex, endIndex, text, marks) => {
        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return
        }

        const doc = activeEditor.state.doc
        const from = getDocPositionFromTextOffset(doc, startIndex)
        const to = getDocPositionFromTextOffset(doc, endIndex)
        const markList = []

        if (marks && marks.color) {
            markList.push({
                type: 'textStyle',
                attrs: { color: marks.color }
            })
        }

        if (marks && marks.highlight) {
            markList.push({
                type: 'highlight',
                attrs: { color: marks.highlight }
            })
        }

        const content = markList.length
            ? [{ type: 'text', text, marks: markList }]
            : text

        activeEditor.focus()
        activeEditor.commands.insertContent(content, {
            position: { from, to }
        })
    }

    const logHighlightDebug = (clause, index) => {
        const activeEditor = getActiveEditor()
        if (!activeEditor || !clause) {
            return
        }

        const doc = activeEditor.state.doc
        const from = getDocPositionFromTextOffset(doc, clause.startIndex)
        const to = getDocPositionFromTextOffset(doc, clause.endIndex)
        const excerpt = clause.clauseText
            ? clause.clauseText.slice(0, 120)
            : ''

        console.log('highlight debug', {
            index,
            clauseType: clause.clauseType,
            startIndex: clause.startIndex,
            endIndex: clause.endIndex,
            from,
            to,
            excerpt
        })
    }

    useEffect(() => {
        setContractTypes([
            COLORADO_PRIVACY_LAW,
            VIRGINIA_PRIVACY_LAW,
            CALIFORNIA_PRIVACY_LAW
        ])
    }, [])

    useEffect(() => {
        if (analysisStartedAt == null) {
            return undefined
        }

        const updateElapsed = () => {
            setAnalysisElapsedSeconds(
                Math.floor((Date.now() - analysisStartedAt) / 1000)
            )
        }
        updateElapsed()
        const interval = window.setInterval(updateElapsed, 1000)
        return () => window.clearInterval(interval)
    }, [analysisStartedAt])

    useEffect(() => {
        if (!analysisPending) {
            return
        }
        if (!hasUploadedDocument || !isEditorReady) {
            return
        }
        runDocumentAnalysis(analysisPending)
    }, [analysisPending, hasUploadedDocument, isEditorReady])

    useEffect(() => {
        if (superdocInstance?.setActiveEditor && visibleEditor) {
            superdocInstance.setActiveEditor(visibleEditor)
        }
    }, [superdocInstance, visibleEditor])

    useEffect(() => {
        if (isMobileViewport) {
            setDocumentViewportWidth(null)
            return undefined
        }
        if (!isEditorReady || !editorCanvasRef.current) {
            setDocumentViewportWidth(DEFAULT_DOCUMENT_VIEWPORT_WIDTH)
            return undefined
        }

        const canvas = editorCanvasRef.current
        let observedPage = null
        const resizeObserver =
            typeof ResizeObserver === 'function'
                ? new ResizeObserver(() => measurePage())
                : null

        const measurePage = () => {
            const page = canvas.querySelector('.superdoc-page')
            if (!(page instanceof HTMLElement)) {
                return
            }
            if (page !== observedPage) {
                if (observedPage) {
                    resizeObserver?.unobserve(observedPage)
                }
                observedPage = page
                resizeObserver?.observe(page)
            }

            const width = Math.ceil(page.getBoundingClientRect().width)
            if (width > 0) {
                setDocumentViewportWidth(previous =>
                    previous === width ? previous : width
                )
            }
        }

        const mutationObserver = new MutationObserver(measurePage)
        mutationObserver.observe(canvas, { childList: true, subtree: true })
        window.addEventListener('resize', measurePage)
        const frame = requestAnimationFrame(measurePage)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('resize', measurePage)
            mutationObserver.disconnect()
            resizeObserver?.disconnect()
        }
    }, [isEditorReady, isMobileViewport, documentFile])


    const handleFileUpload = async e => {
        if (!e.target.files) {
            return
        }
        const file = e.target.files[0]
        if (!file) {
            return
        }
        try {
            setSuperdocInstance(null)
            setVisibleEditor(null)
            setIsEditorReady(false)
            setAnalysisPending(null)
            setDocumentFile(file)
            setHasUploadedDocument(true)
            setSelectedContractType('')
            setPendingContractType('')
            setOpenSettings(true)
            setAnalysis('')
            setHasTabResult(false)
        } catch (error) {
            console.error('DOCX import failed', error)
        } finally {
            e.target.value = null
        }
    }


    const renderAnalysisActions = ans => {
        setAnalysis(ans.analysis)
        let actions = ans.actions || []
        for (let action of actions) {
            if (action.deleteText) {
                let startIndex = action['startIndex']
                let endIndex = action['endIndex']
                console.log('replace text startindex ' + startIndex)
                console.log(action.deleteText)
                console.log('replace text endIndex' + endIndex)
                replaceTextRange(startIndex, endIndex, action.replaceText, {
                    color: 'green',
                    highlight: 'yellow'
                })
            }
        }
    }

    const tintHex = (hex, amount = 0.7) => {
        if (!hex) {
            return '#e3f2fd'
        }
        const cleaned = hex.replace('#', '')
        if (cleaned.length !== 6) {
            return hex
        }
        const r = parseInt(cleaned.slice(0, 2), 16)
        const g = parseInt(cleaned.slice(2, 4), 16)
        const b = parseInt(cleaned.slice(4, 6), 16)
        const mix = channel => Math.round(channel + (255 - channel) * amount)
        const toHex = channel => channel.toString(16).padStart(2, '0')
        return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`
    }

    const extractPlainText = html => {
        if (!html) {
            return ''
        }
        const container = document.createElement('div')
        container.innerHTML = html
        const textWithBlockBoundaries = Array.from(container.childNodes)
            .map(node => node.textContent || '')
            .join('\n')
        return (container.innerText || textWithBlockBoundaries).trim()
    }

    const getFindingAppearance = analysisHtml => {
        const plainText = extractPlainText(analysisHtml)
        const outcome = getFindingOutcome(plainText)
        const accent = FINDING_OUTCOME_STYLES[outcome].accent
        return {
            plainText,
            outcome,
            baseColor: accent,
            color: tintHex(accent, 0.72),
            panelColor: tintHex(accent, 0.93),
            panelActiveColor: tintHex(accent, 0.86)
        }
    }

    const clearCommentAnchor = commentId => {
        if (!commentId) {
            return
        }
        const existing = document.querySelector(
            `[data-comment-anchor="${String(commentId)}"]`
        )
        if (existing) {
            existing.removeAttribute('id')
            existing.removeAttribute('data-comment-anchor')
        }
    }

    const getPresentationVisibleHost = presentationEditor => {
        const visibleHost =
            presentationEditor?.visibleHost ||
            presentationEditor?.presentationEditor?.visibleHost
        return visibleHost instanceof HTMLElement ? visibleHost : null
    }

    const getEditorViewportElement = presentationEditor => {
        const visibleHost = getPresentationVisibleHost(presentationEditor)
        if (visibleHost) {
            const viewport = visibleHost.closest('.editor-canvas')
            if (viewport instanceof HTMLElement) {
                return viewport
            }
        }

        const editorCanvas = document.querySelector('.editor-canvas')
        return editorCanvas instanceof HTMLElement ? editorCanvas : null
    }

    const getRenderedPageNumberAtPosition = position => {
        const presentationEditor = getPresentationEditor()
        if (typeof presentationEditor?.coordsAtPos !== 'function') {
            return null
        }

        const coords = presentationEditor.coordsAtPos(position)
        const visibleHost = getPresentationVisibleHost(presentationEditor)
        if (!coords || !(visibleHost instanceof HTMLElement)) {
            return null
        }

        const pageElements = Array.from(
            visibleHost.querySelectorAll('.superdoc-page[data-page-index]')
        )
        if (!pageElements.length) {
            return null
        }

        let bestPageNumber = null
        let bestDistance = Number.POSITIVE_INFINITY

        for (const pageElement of pageElements) {
            if (!(pageElement instanceof HTMLElement)) {
                continue
            }
            const pageIndex = Number(pageElement.getAttribute('data-page-index'))
            if (!Number.isFinite(pageIndex)) {
                continue
            }
            const rect = pageElement.getBoundingClientRect()
            const withinPage = coords.top >= rect.top && coords.top <= rect.bottom
            if (withinPage) {
                return pageIndex + 1
            }

            const distance =
                coords.top < rect.top
                    ? rect.top - coords.top
                    : coords.top - rect.bottom
            if (distance < bestDistance) {
                bestDistance = distance
                bestPageNumber = pageIndex + 1
            }
        }

        return bestPageNumber
    }

    const getRenderedPageElement = pageNumber => {
        const presentationEditor = getPresentationEditor()
        const visibleHost = getPresentationVisibleHost(presentationEditor)
        if (!(visibleHost instanceof HTMLElement) || !Number.isInteger(pageNumber)) {
            return null
        }

        return visibleHost.querySelector(
            `.superdoc-page[data-page-index="${pageNumber - 1}"]`
        )
    }

    const getClauseScrollContainer = () => {
        const presentationEditor = getPresentationEditor()
        const visibleHost = getPresentationVisibleHost(presentationEditor)
        const start =
            visibleHost?.querySelector?.('.superdoc-page') ||
            visibleHost ||
            document.querySelector('.presentation-editor__pages') ||
            document.querySelector('.editor-canvas')

        let current = start instanceof HTMLElement ? start : null
        while (current) {
            const style = window.getComputedStyle(current)
            const canScroll =
                ['auto', 'scroll', 'overlay'].includes(style.overflowY) &&
                current.scrollHeight > current.clientHeight
            if (canScroll) {
                return current
            }
            current = current.parentElement
        }

        const editorCanvas = document.querySelector('.editor-canvas')
        return editorCanvas instanceof HTMLElement ? editorCanvas : null
    }

    const waitForNextFrame = () =>
        new Promise(resolve => {
            requestAnimationFrame(() => resolve())
        })

    const scrollCanvasToRenderedPage = pageNumber => {
        const viewportElement = getClauseScrollContainer()
        const pageElement = getRenderedPageElement(pageNumber)
        if (!(viewportElement instanceof HTMLElement) || !(pageElement instanceof HTMLElement)) {
            return false
        }

        const viewportRect = viewportElement.getBoundingClientRect()
        const pageRect = pageElement.getBoundingClientRect()
        const delta = pageRect.top - viewportRect.top - 20

        viewportElement.scrollBy({
            top: delta,
            behavior: 'auto'
        })

        return true
    }

    const getScrollContainers = (element, presentationEditor = null) => {
        if (!element) {
            return []
        }

        const containers = []
        const seen = new Set()

        const addContainer = candidate => {
            if (!(candidate instanceof HTMLElement) || seen.has(candidate)) {
                return
            }
            seen.add(candidate)
            containers.push(candidate)
        }

        const visibleHost = getPresentationVisibleHost(presentationEditor)
        if (visibleHost) {
            addContainer(visibleHost)
        }

        const editorCanvas = document.querySelector('.editor-canvas')
        if (editorCanvas instanceof HTMLElement) {
            addContainer(editorCanvas)
        }

        let current = element.parentElement
        while (current) {
            const style = window.getComputedStyle(current)
            const overflowY = style?.overflowY || ''
            if (
                ['auto', 'scroll', 'overlay'].includes(overflowY) &&
                current.scrollHeight > current.clientHeight
            ) {
                addContainer(current)
            }
            current = current.parentElement
        }

        return containers
    }

    const getNearestScrollContainer = (element, presentationEditor = null) => {
        const containers = getScrollContainers(element, presentationEditor)
        return containers[0] || null
    }

    const setCommentAnchor = (commentId, element) => {
        if (!commentId || !element) {
            return null
        }

        clearCommentAnchor(commentId)
        element.setAttribute('id', `comment-anchor-${String(commentId)}`)
        element.setAttribute('data-comment-anchor', String(commentId))
        return element
    }

    const getCommentAnchorElement = (editor, position, options = {}) => {
        const view = editor?.view
        if (!view || position == null) {
            return null
        }

        const blockSelector =
            'p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, table, tr, td, th, div'

        const presentationElementAtPos = getPresentationEditor()?.getElementAtPos?.(
            position,
            options
        )
        if (presentationElementAtPos instanceof HTMLElement) {
            return (
                presentationElementAtPos.closest(blockSelector) ||
                presentationElementAtPos
            )
        }

        const editorElementAtPos = editor?.getElementAtPos?.(position, options)
        if (editorElementAtPos instanceof HTMLElement) {
            return editorElementAtPos.closest(blockSelector) || editorElementAtPos
        }

        const nodeDom = view.nodeDOM?.(position)
        if (nodeDom instanceof HTMLElement) {
            return nodeDom.closest(blockSelector) || nodeDom
        }
        if (nodeDom?.parentElement) {
            return nodeDom.parentElement.closest(blockSelector) || nodeDom.parentElement
        }

        const domAtPos = view.domAtPos?.(position)
        const domNode = domAtPos?.node
        if (domNode instanceof HTMLElement) {
            return domNode.closest(blockSelector) || domNode
        }
        if (domNode?.parentElement) {
            return domNode.parentElement.closest(blockSelector) || domNode.parentElement
        }

        return view.dom instanceof HTMLElement ? view.dom : null
    }

    const alignCommentAnchorToTop = (activeEditor, commentId, position) => {
        const refreshedAnchorElement = setCommentAnchor(
            commentId,
            getCommentAnchorElement(activeEditor, position, {
                forceRebuild: true,
                fallbackToCoords: true
            })
        )

        if (!refreshedAnchorElement) {
            return false
        }

        refreshedAnchorElement.scrollIntoView({
            behavior: 'auto',
            block: 'start',
            inline: 'nearest'
        })

        return scrollEditorPositionToTop(
            activeEditor,
            position,
            refreshedAnchorElement
        )
    }

    const alignCommentPositionToViewportTop = (position, attempt = 0) => {
        const presentationEditor = getPresentationEditor()
        const viewportElement = getEditorViewportElement(presentationEditor)

        if (
            !(viewportElement instanceof HTMLElement) ||
            typeof presentationEditor?.coordsAtPos !== 'function'
        ) {
            return false
        }

        const coords = presentationEditor.coordsAtPos(position)
        if (!coords) {
            return false
        }

        const viewportRect = viewportElement.getBoundingClientRect()
        const visibleViewportTop = Math.max(viewportRect.top, 0)
        const desiredTop = visibleViewportTop + 20
        const delta = coords.top - desiredTop

        console.log('comment presentation align debug', {
            position,
            attempt,
            currentViewportScrollTop: viewportElement.scrollTop,
            viewportTop: viewportRect.top,
            visibleViewportTop,
            desiredTop,
            coordsTop: coords.top,
            delta
        })

        if (Math.abs(delta) <= 4) {
            return true
        }

        viewportElement.scrollBy({
            top: delta,
            behavior: 'auto'
        })

        console.log('comment presentation align debug', {
            position,
            attempt,
            nextViewportScrollTop: viewportElement.scrollTop,
            appliedDelta: delta
        })

        if (attempt < 4) {
            requestAnimationFrame(() => {
                alignCommentPositionToViewportTop(position, attempt + 1)
            })
        }

        return true
    }

    const getCommentViewportPlacement = (from, to) => {
        const presentationEditor = getPresentationEditor()
        const viewportElement = getClauseScrollContainer()

        if (
            !(viewportElement instanceof HTMLElement) ||
            typeof presentationEditor?.coordsAtPos !== 'function'
        ) {
            return null
        }

        const startCoords = presentationEditor.coordsAtPos(from)
        const endCoords = presentationEditor.coordsAtPos(to)
        if (!startCoords || !endCoords) {
            return null
        }

        const viewportRect = viewportElement.getBoundingClientRect()
        const bottomGap = viewportRect.bottom - endCoords.bottom
        const topGap = startCoords.top - viewportRect.top
        const isStartNearBottom =
            startCoords.top >= viewportRect.bottom - 40 &&
            startCoords.top <= viewportRect.bottom + 8
        const isRangeBottomAtViewportBottom = Math.abs(bottomGap) <= 8
        const isRangeStartNearTop = Math.abs(startCoords.top - (viewportRect.top + 20)) <= 20

        return {
            viewportElement,
            viewportRect,
            startCoords,
            endCoords,
            bottomGap,
            topGap,
            isStartNearBottom,
            isRangeBottomAtViewportBottom,
            isRangeStartNearTop
        }
    }

    const logCommentViewportPlacement = (from, to, commentId) => {
        const placement = getCommentViewportPlacement(from, to)
        if (!placement) {
            return null
        }

        const {
            viewportRect,
            startCoords,
            endCoords,
            bottomGap,
            topGap,
            isStartNearBottom,
            isRangeBottomAtViewportBottom,
            isRangeStartNearTop
        } = placement

        console.log('comment viewport placement debug', {
            commentId,
            from,
            to,
            viewportTop: viewportRect.top,
            viewportBottom: viewportRect.bottom,
            rangeStartTop: startCoords.top,
            rangeStartBottom: startCoords.bottom,
            rangeEndTop: endCoords.top,
            rangeEndBottom: endCoords.bottom,
            topGap,
            bottomGap,
            isStartNearBottom,
            isRangeBottomAtViewportBottom,
            isRangeStartNearTop
        })

        return placement
    }

    const correctCommentViewportPlacement = (from, to, commentId, attempt = 0) => {
        const placement = logCommentViewportPlacement(from, to, commentId)
        if (!placement) {
            return false
        }

        const { viewportElement, viewportRect, startCoords, isStartNearBottom, isRangeStartNearTop } = placement
        if (!isStartNearBottom && isRangeStartNearTop) {
            return true
        }

        if (attempt >= 1) {
            return true
        }

        const correctionDelta = startCoords.top - (viewportRect.top + 20)
        if (Math.abs(correctionDelta) <= 4) {
            return true
        }

        viewportElement.scrollBy({
            top: correctionDelta,
            behavior: 'auto'
        })

        console.log('comment viewport correction debug', {
            commentId,
            from,
            to,
            attempt,
            correctionDelta,
            reason: isStartNearBottom
                ? 'start-near-bottom'
                : 'start-not-near-top'
        })

        requestAnimationFrame(() => {
            correctCommentViewportPlacement(from, to, commentId, attempt + 1)
        })

        return true
    }

    const positionClauseNearTop = async (from, to, commentId) => {
        await waitForNextFrame()

        const firstPlacement = getCommentViewportPlacement(from, to)
        if (!firstPlacement) {
            return false
        }

        const { viewportElement, viewportRect, startCoords } = firstPlacement
        const firstDelta = startCoords.top - (viewportRect.top + 20)

        if (Math.abs(firstDelta) > 4) {
            viewportElement.scrollBy({
                top: firstDelta,
                behavior: 'auto'
            })
        }

        await waitForNextFrame()
        correctCommentViewportPlacement(from, to, commentId, 0)
        return true
    }

    const getElementTopWithinContainer = (element, container) => {
        if (!element || !container) {
            return null
        }

        let top = 0
        let current = element

        while (current && current !== container) {
            if (!(current instanceof HTMLElement)) {
                return null
            }
            top += current.offsetTop
            current = current.offsetParent
        }

        if (current !== container) {
            const elementRect = element.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            return container.scrollTop + (elementRect.top - containerRect.top)
        }

        return top
    }

    const scrollEditorViewportToPosition = async position => {
        const presentationEditor = getPresentationEditor()
        if (!presentationEditor || position == null) {
            console.log('comment presentation debug', {
                position,
                reason: 'missing-presentation-editor',
                hasSuperdocPresentationEditor: !!superdocInstance?.presentationEditor,
                hasVisibleEditorPresentationEditor: !!visibleEditor?.presentationEditor,
                visibleEditorType: visibleEditor?.constructor?.name || null,
                superdocEditorType: superdocInstance?.editor?.constructor?.name || null
            })
            return false
        }

        const scrollOptions = { block: 'start', behavior: 'auto' }
        const visibleHost = getPresentationVisibleHost(presentationEditor)
        const beforeCoords =
            typeof presentationEditor.coordsAtPos === 'function'
                ? presentationEditor.coordsAtPos(position)
                : null
        const beforeScrollTop = visibleHost?.scrollTop ?? null
        const editorType = presentationEditor?.constructor?.name || null

        if (typeof presentationEditor.scrollToPositionAsync === 'function') {
            const didScrollToPositionAsync =
                await presentationEditor.scrollToPositionAsync(
                    position,
                    scrollOptions
                )
            let didScroll = didScrollToPositionAsync
            let pageNumberFallback = null
            let didScrollPageFallback = false
            let didScrollPositionRetry = false

            if (!didScroll) {
                pageNumberFallback = getRenderedPageNumberAtPosition(position)
                if (Number.isInteger(pageNumberFallback)) {
                    didScrollPageFallback = scrollCanvasToRenderedPage(
                        pageNumberFallback
                    )
                    if (didScrollPageFallback) {
                        didScrollPositionRetry =
                            presentationEditor.scrollToPosition?.(
                                position,
                                scrollOptions
                            ) || false
                        didScroll = didScrollPositionRetry || didScrollPageFallback
                    }
                }
            }

            const afterCoords =
                typeof presentationEditor.coordsAtPos === 'function'
                    ? presentationEditor.coordsAtPos(position)
                    : null
            const afterScrollTop = visibleHost?.scrollTop ?? null
            console.log('comment presentation debug', {
                position,
                editorType,
                method: 'scrollToPositionAsync',
                didScroll,
                didScrollToPositionAsync,
                pageNumberFallback,
                didScrollPageFallback,
                didScrollPositionRetry,
                beforeScrollTop,
                afterScrollTop,
                beforeCoords,
                afterCoords,
                hasVisibleHost: !!visibleHost
            })
            return didScroll
        }

        if (typeof presentationEditor.scrollToPosition === 'function') {
            const didScroll = presentationEditor.scrollToPosition(
                position,
                scrollOptions
            )
            const afterCoords =
                typeof presentationEditor.coordsAtPos === 'function'
                    ? presentationEditor.coordsAtPos(position)
                    : null
            const afterScrollTop = visibleHost?.scrollTop ?? null
            console.log('comment presentation debug', {
                position,
                editorType,
                method: 'scrollToPosition',
                didScroll,
                beforeScrollTop,
                afterScrollTop,
                beforeCoords,
                afterCoords,
                hasVisibleHost: !!visibleHost
            })
            return didScroll
        }

        console.log('comment presentation debug', {
            position,
            editorType,
            reason: 'missing-scroll-methods',
            hasScrollToPositionAsync:
                typeof presentationEditor.scrollToPositionAsync === 'function',
            hasScrollToPosition:
                typeof presentationEditor.scrollToPosition === 'function',
            hasCoordsAtPos: typeof presentationEditor.coordsAtPos === 'function',
            hasVisibleHost: !!visibleHost
        })

        return false
    }

    const scrollEditorPositionToTop = (editor, position, anchorElement) => {
        const view = editor?.view
        if (!view || position == null) {
            return false
        }

        if (anchorElement?.scrollIntoView) {
            anchorElement.scrollIntoView({
                behavior: 'auto',
                block: 'start',
                inline: 'nearest'
            })
        }

        const scrollContainer = getNearestScrollContainer(
            anchorElement || view.dom,
            getPresentationEditor()
        )
        if (!scrollContainer) {
            return !!anchorElement
        }

        let didAttemptScroll = false
        const scrollContainerToTarget = () => {
            const containerRect = scrollContainer.getBoundingClientRect()
            const anchorRect = anchorElement?.getBoundingClientRect?.()
            const targetTop = getElementTopWithinContainer(
                anchorElement,
                scrollContainer
            )
            if (targetTop == null || !anchorRect) {
                return
            }
            const currentScrollTop = scrollContainer.scrollTop
            const nextScrollTop = Math.max(0, targetTop - 20)
            const isClauseBelowViewport = anchorRect.top > containerRect.bottom
            const isClauseAboveViewport = anchorRect.bottom < containerRect.top
            const isClauseBottomVisibleAtViewportBottom =
                anchorRect.top < containerRect.bottom &&
                anchorRect.bottom > containerRect.bottom
            const isScrollingUp = nextScrollTop < currentScrollTop

            console.log('comment scroll debug', {
                commentId: activeCommentId,
                position,
                currentScrollTop,
                nextScrollTop,
                isScrollingUp,
                isClauseBelowViewport,
                isClauseAboveViewport,
                isClauseBottomVisibleAtViewportBottom,
                anchorTop: anchorRect.top,
                anchorBottom: anchorRect.bottom,
                containerTop: containerRect.top,
                containerBottom: containerRect.bottom
            })

            scrollContainer.scrollTo({
                top: nextScrollTop,
                behavior: 'auto'
            })
            didAttemptScroll = true
        }

        scrollContainerToTarget()
        requestAnimationFrame(scrollContainerToTarget)
        window.setTimeout(scrollContainerToTarget, 50)

        return didAttemptScroll || !!anchorElement
    }

    const scrollToCommentClause = comment => {
        const clauseText = comment?.clauseText?.replace(/\s+/g, ' ').trim()
        console.log('comment scroll debug', {
            commentId: comment?.id,
            clauseType: comment?.clauseType,
            hasClauseText: !!clauseText,
            clauseTextLength: clauseText?.length || 0,
            startIndex: comment?.startIndex,
            endIndex: comment?.endIndex
        })
        if (!clauseText) {
            return false
        }

        const searchQuery = clauseText.slice(0, 160)
        if (!searchQuery) {
            return false
        }

        try {
            const superdocResults = superdocInstance?.search?.(searchQuery)
            if (
                Array.isArray(superdocResults) &&
                superdocResults.length &&
                typeof superdocInstance?.goToSearchResult === 'function'
            ) {
                console.log('comment scroll debug', {
                    commentId: comment?.id,
                    searchQuery,
                    source: 'superdoc.search',
                    resultCount: superdocResults.length
                })
                superdocInstance.goToSearchResult(superdocResults[0])
                return true
            }

            const activeEditor = getActiveEditor()
            const editorResults = activeEditor?.commands?.search?.(searchQuery)
            if (
                Array.isArray(editorResults) &&
                editorResults.length &&
                typeof activeEditor?.commands?.goToSearchResult === 'function'
            ) {
                console.log('comment scroll debug', {
                    commentId: comment?.id,
                    searchQuery,
                    source: 'editor.commands.search',
                    resultCount: editorResults.length
                })
                activeEditor.commands.goToSearchResult(editorResults[0])
                return true
            }

            console.log('comment scroll debug', {
                commentId: comment?.id,
                searchQuery,
                reason: 'no-search-results'
            })
        } catch (error) {
            console.log('comment scroll debug', {
                commentId: comment?.id,
                searchQuery,
                error
            })
        }

        return false
    }

    const applyCommentHighlight = (startIndex, endIndex, color) => {
        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return
        }

        const doc = activeEditor.state.doc
        const from = getDocPositionFromTextOffset(doc, startIndex)
        const to = getDocPositionFromTextOffset(doc, endIndex)
        const highlightMark = activeEditor.state.schema.marks.highlight
        if (!highlightMark) {
            return
        }
        const tr = activeEditor.state.tr.addMark(
            from,
            to,
            highlightMark.create({ color })
        )
        activeEditor.view.dispatch(tr)
    }

    const removeCommentHighlight = (startIndex, endIndex) => {
        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return
        }

        const doc = activeEditor.state.doc
        const from = getDocPositionFromTextOffset(doc, startIndex)
        const to = getDocPositionFromTextOffset(doc, endIndex)
        const highlightMark = activeEditor.state.schema.marks.highlight
        if (!highlightMark) {
            return
        }
        const tr = activeEditor.state.tr.removeMark(from, to, highlightMark)
        activeEditor.view.dispatch(tr)
    }

    const addComment = (analysisHtml, clause) => {
        const id = commentIdRef.current++
        const appearance = getFindingAppearance(analysisHtml)
        const comment = {
            id,
            clauseType: clause?.clauseType || 'Clause Analysis',
            analysisHtml,
            ...appearance,
            clauseText: clause?.clauseText || '',
            recipeClauseType: clause?.clauseType || '',
            startIndex: clause?.startIndex ?? null,
            endIndex: clause?.endIndex ?? null
        }

        setComments(prev => [...prev, comment])
    }

    const getAnalysisPromptKey = clauseType =>
        `${selectedContractType}\u0000${clauseType}`

    const openAnalysisPromptDialog = async comment => {
        const clauseType = comment.recipeClauseType
        const promptKey = getAnalysisPromptKey(clauseType)
        const savedPrompt = analysisPromptsByClause[promptKey]
        setAnalysisPromptDialog({
            open: true,
            commentId: comment.id,
            clauseType,
            draft: savedPrompt || '',
            loading: savedPrompt == null,
            rerunning: false,
            error: ''
        })
        if (savedPrompt != null) {
            return
        }

        try {
            const response = await axios.get('/debug/prompts', {
                params: {
                    contractType: selectedContractType,
                    clauseType
                }
            })
            const prompt = response?.data?.analysisPrompt || ''
            setAnalysisPromptsByClause(previous => ({
                ...previous,
                [promptKey]: prompt
            }))
            setAnalysisPromptDialog(previous => ({
                ...previous,
                draft: prompt,
                loading: false
            }))
        } catch (error) {
            setAnalysisPromptDialog(previous => ({
                ...previous,
                loading: false,
                error: 'Unable to load the clause analysis Markdown.'
            }))
        }
    }

    const rerunCommentAnalysis = async () => {
        const { commentId, clauseType, draft } = analysisPromptDialog
        if (!draft.trim()) {
            return
        }
        const promptKey = getAnalysisPromptKey(clauseType)
        setAnalysisPromptsByClause(previous => ({
            ...previous,
            [promptKey]: draft
        }))
        setAnalysisPromptDialog(previous => ({
            ...previous,
            rerunning: true,
            error: ''
        }))

        const comment = comments.find(item => item.id === commentId)
        const clauseMatch = identifiedClauses[clauseType]
        try {
            const response = await axios.post('/analyze_clause_recipe', {
                contractType: selectedContractType,
                clauseType,
                clauseText: clauseMatch?.clauseText || comment?.clauseText || '',
                document: getEditorText(),
                analysisModel,
                analysisEffort,
                analysisPrompt: draft
            })
            const analysisHtml = response?.data?.analysis || ''
            const appearance = getFindingAppearance(analysisHtml)
            setComments(previous =>
                previous.map(item =>
                    item.id === commentId
                        ? { ...item, analysisHtml, ...appearance }
                        : item
                )
            )
            if (
                activeCommentId === commentId &&
                comment?.startIndex != null &&
                comment?.endIndex != null
            ) {
                removeCommentHighlight(comment.startIndex, comment.endIndex)
                applyCommentHighlight(
                    comment.startIndex,
                    comment.endIndex,
                    appearance.color
                )
            }
            setAnalysisPromptDialog(previous => ({
                ...previous,
                open: false,
                rerunning: false
            }))
        } catch (error) {
            setAnalysisPromptDialog(previous => ({
                ...previous,
                rerunning: false,
                error: 'Unable to rerun the clause analysis.'
            }))
        }
    }

    const scrollToCommentPosition = async comment => {
        const editor = getEditorApi()
        if (!editor?.state?.doc || !editor?.view) {
            return false
        }

        const doc = editor.state.doc
        const maxPos = doc.content?.size || 0
        const from = Math.max(
            1,
            Math.min(getDocPositionFromTextOffset(doc, comment.startIndex), maxPos)
        )
        const to = Math.max(
            from,
            Math.min(getDocPositionFromTextOffset(doc, comment.endIndex), maxPos)
        )

        editor.focus?.()
        editor.commands?.focus?.()
        if (typeof editor.commands?.setTextSelection === 'function') {
            editor.commands.setTextSelection({ from, to: from })
        }

        const didScrollWithPresentationApi = await scrollEditorViewportToPosition(from)
        if (didScrollWithPresentationApi) {
            return await positionClauseNearTop(from, to, comment.id)
        }

        const didAlignWithPresentationCoords = alignCommentPositionToViewportTop(from, 0)
        if (didAlignWithPresentationCoords) {
            return await positionClauseNearTop(from, to, comment.id)
        }

        const pageNumberFallback = getRenderedPageNumberAtPosition(from)
        if (Number.isInteger(pageNumberFallback) && scrollCanvasToRenderedPage(pageNumberFallback)) {
            console.log('comment presentation debug', {
                commentId: comment.id,
                position: from,
                reason: 'used-rendered-page-fallback',
                pageNumberFallback
            })
            return await positionClauseNearTop(from, to, comment.id)
        }

        console.log('comment presentation debug', {
            commentId: comment.id,
            position: from,
            reason: 'presentation-scroll-unavailable'
        })

        return false
    }

    const focusComment = comment => {
        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return
        }

        if (comment?.startIndex == null || comment?.endIndex == null) {
            if (expandedCommentId && expandedCommentId !== comment.id) {
                const previous = comments.find(item => item.id === expandedCommentId)
                if (previous?.startIndex != null && previous?.endIndex != null) {
                    removeCommentHighlight(previous.startIndex, previous.endIndex)
                    clearCommentAnchor(previous.id)
                }
            }
            if (activeCommentId && activeCommentId !== comment.id) {
                const prev = comments.find(item => item.id === activeCommentId)
                if (prev?.startIndex != null && prev?.endIndex != null) {
                    removeCommentHighlight(prev.startIndex, prev.endIndex)
                    clearCommentAnchor(prev.id)
                }
            }
            setActiveCommentId(null)
            if (expandedCommentId === comment.id) {
                setExpandedCommentId(null)
            } else {
                setExpandedCommentId(comment.id)
            }
            return
        }

        if (expandedCommentId && expandedCommentId !== comment.id) {
            const previous = comments.find(item => item.id === expandedCommentId)
            if (previous?.startIndex != null && previous?.endIndex != null) {
                removeCommentHighlight(previous.startIndex, previous.endIndex)
                clearCommentAnchor(previous.id)
            }
        }
        if (expandedCommentId !== comment.id) {
            applyCommentHighlight(comment.startIndex, comment.endIndex, comment.color)
        }

        if (activeCommentId === comment.id) {
            if (comment?.startIndex != null && comment?.endIndex != null) {
                removeCommentHighlight(comment.startIndex, comment.endIndex)
                clearCommentAnchor(comment.id)
            }
            setActiveCommentId(null)
            setExpandedCommentId(null)
            return
        }

        setActiveCommentId(comment.id)
        setExpandedCommentId(comment.id)
        pendingScrollCommentIdRef.current = comment.id
    }

    React.useLayoutEffect(() => {
        const pendingCommentId = pendingScrollCommentIdRef.current
        if (!pendingCommentId) {
            return
        }
        if (activeCommentId !== pendingCommentId || expandedCommentId !== pendingCommentId) {
            return
        }

        const comment = comments.find(item => item.id === pendingCommentId)
        if (!comment) {
            pendingScrollCommentIdRef.current = null
            return
        }

        pendingScrollCommentIdRef.current = null
        let cancelled = false

        const runPendingScroll = async () => {
            const didScroll = await scrollToCommentPosition(comment)
            if (cancelled || didScroll) {
                return
            }
            scrollToCommentClause(comment)
        }

        runPendingScroll()

        return () => {
            cancelled = true
        }
    }, [activeCommentId, expandedCommentId, comments])

    const summarize = async newClauseType => {
        setClauseType(newClauseType)

        if (serverErrors > 5) {
            setAnalysis('Error, try again later.')
            return
        }
        setIsLoading(true)
        setAnalysis('Loading...')
        let text = getEditorText()
        console.log(text)
        let data = { clauseType: newClauseType, document: text }
        try {
            let res = await axios.post('/summarize', data)
            let ans = res.data

            console.log('response')
            console.log(ans)
            renderAnalysisActions(ans)
            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            summarize(clauseType)
            setAnalysis('Trying, trying, trying. Please be patient.')
        }

        setHasTabResult(true)
        setIsLoading(false)
    }

    const paraphrase = async newClauseType => {
        setClauseType(newClauseType)
        // update steps

        // TODO: favorability steps are currently disabled without schema data
        paraphraseModalHandleOpen()
        if (serverErrors > 5) {
            setAnalysis('Error, try again later.')
            return
        }

        let text = getEditorText()
        console.log(text)
        setIsLoading(true)
        //setAnalysis('Loading...')
        let data = { clauseType: newClauseType, document: text }

        try {
            let res = await axios.post('/identify_clause', data)
            let ans = res.data

            //alert(JSON.stringify(ans))
            console.log('response')
            // contains start and stop index
            console.log(ans)
            //renderAnalysisActions(ans)
            if (ans.clause && ans.clause.clause) {
                setClauseObj(ans.clause)
                let clause = ans.clause.clause
                setClause(clause)
                setServerErrors(0)

                let clauseObj = ans.clause
                if (clauseObj && clauseObj.startIndex) {
                    let startIndex = clauseObj['startIndex']
                    let endIndex = clauseObj['endIndex']
                    console.log('replace text startindex ' + startIndex)
                    console.log('replace text endIndex' + endIndex)
                    replaceTextRange(startIndex, endIndex, clause, {
                        highlight: '#d3d3d3'
                    })
                }
            } else {
                setClause(
                    'Could not find clause. Please try a different clause.'
                )
                console.log('errors')
                setServerErrors(1)
            }
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            //paraphrase(clauseType)
            //setAnalysis('Trying, trying, trying. Please be patient.')
        }

        /*
        try {
            let res = await axios.post('/paraphrase', data)
            let ans = res.data

            console.log('response')
            console.log(ans)
            renderAnalysisActions(ans)
            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            paraphrase(clauseType)
            setAnalysis('Trying, trying, trying. Please be patient.')
        }
        */

        setIsLoading(false)
    }

    const exploreTopic = async topic => {
        if (serverErrors > 5) {
            setAnalysis('Error, try again later.')
            return
        }

        setIsLoading(true)
        setAnalysis('Loading...')

        const text = getEditorText()
        const clauseType = topic?.clauseType
        const clauseMatch = clauseType ? identifiedClauses[clauseType] : null
        const clauseText = clauseMatch?.clauseText || ''

        const data = {
            contractType: selectedContractType,
            topicName: topic?.topicName,
            clauseText,
            document: text
        }

        try {
            const res = await axios.post('/explore_topic_analysis', data)
            const ans = res.data
            setAnalysis(ans?.analysis || '')
            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            setAnalysis('Trying, trying, trying. Please be patient.')
        }

        setHasTabResult(true)
        setIsLoading(false)
    }

    // open paraphrase

    const analyze = async (newClauseType, contractType) => {
        let clauseType = newClauseType
        setClauseType(newClauseType)

        if (serverErrors > 5) {
            setAnalysis('Error, try again later.')
            return
        }
        setIsLoading(true)
        setAnalysis('Loading...')
        let text = getEditorText()
        console.log(text)
        const clauseMatch = identifiedClauses[newClauseType]
        let data = {
            contractType,
            clauseType,
            clauseText: clauseMatch?.clauseText || '',
            document: text,
            analysisModel,
            analysisEffort
        }
        try {
            let res = await axios.post('/analyze_clause_recipe', data)
            let ans = res.data

            console.log('response')
            console.log(ans)
            renderAnalysisActions(ans)
            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
            setAnalysis('Trying, trying, trying. Please be patient.')
        }

        setHasTabResult(true)
        setIsLoading(false)
    }

    const runDocumentAnalysis = async contractType => {
        if (serverErrors > 5) {
            setAnalysisPending(null)
            setAnalysis('Error, try again later.')
            return
        }

        const activeEditor = getActiveEditor()
        if (!activeEditor) {
            return
        }
        setAnalysisPending(null)

        setComments([])
        setActiveCommentId(null)
        setExpandedCommentId(null)
        commentIdRef.current = 1
        setIsLoading(true)
        setAnalysis('Loading...')
        setAnalysisElapsedSeconds(0)
        setIdentificationDurationMs(null)
        setClauseAnalysisDurationMs(null)
        setTotalAnalysisDurationMs(null)
        const documentAnalysisStartedAt = Date.now()
        setAnalysisStartedAt(documentAnalysisStartedAt)

        const documentText = getEditorText()
        const documentHtml = getEditorHtml()
        const clauseTypes = lawClauseNames[contractType] || []

        //alert(JSON.stringify(documentText))
        //alert(JSON.stringify(clauseTypes))
        try {
            const identificationStartedAt = Date.now()
            let res
            try {
                res = await axios.post('/identify_clauses', {
                    document: documentText,
                    documentHtml,
                    clauseTypes,
                    contractType,
                    identificationModel,
                    identificationEffort
                })
            } finally {
                setIdentificationDurationMs(Date.now() - identificationStartedAt)
            }
            const ans = res.data
            const clauses = ans?.clauses || []

            const clauseMap = {}
            clauses.forEach(clause => {
                if (clause?.clauseType) {
                    clauseMap[clause.clauseType] = clause
                }
            })
            setIdentifiedClauses(clauseMap)

            clauses.forEach((clause, index) => {
                if (!clause || clause.startIndex == null || clause.endIndex == null) {
                    return
                }
                logHighlightDebug(clause, index)
            })

            const foundClauses = clauses
                .filter(clause => clause && clause.startIndex != null)
                .sort((a, b) => b.startIndex - a.startIndex)
            const missingClauses = clauses.filter(
                clause => !clause || clause.startIndex == null
            )

            if (ANALYZE_CLAUSE_RECIPE_ENABLED) {
                const clauseAnalysisStartedAt = Date.now()
                try {
                    await runStaggeredTasks(
                        [...foundClauses, ...missingClauses],
                        async clause => {
                            try {
                                const analysisRes = await axios.post(
                                    '/analyze_clause_recipe',
                                    {
                                        contractType,
                                        clauseType: clause?.clauseType,
                                        clauseText: clause?.clauseText || '',
                                        document: documentText,
                                        analysisModel,
                                        analysisEffort
                                    }
                                )
                                const analysisHtml =
                                    analysisRes?.data?.analysis || ''
                                addComment(analysisHtml, clause)
                            } catch (error) {
                                console.log('analysis error')
                                console.log(error)
                            }
                        }
                    )
                } finally {
                    setClauseAnalysisDurationMs(
                        Date.now() - clauseAnalysisStartedAt
                    )
                }
            }

            setServerErrors(0)
        } catch (e) {
            console.log('network error')
            console.log(e)
            setServerErrors(serverErrors + 1)
        }

        setIsLoading(false)
        setTotalAnalysisDurationMs(Date.now() - documentAnalysisStartedAt)
        setAnalysisStartedAt(null)
    }

    /*
    const [value, setValue] = React.useState('1')

    const handleTabChange = (event, newValue) => {
        setValue(newValue)
    }
    */

    const [tabValue, setTabValue] = React.useState(0)

    const handleTabChange = (event, newValue) => {
        if (newValue === 0 && !supportsExplore(selectedContractType)) {
            return
        }
        setTabValue(newValue)

        let tab = 'analysis'
        if (newValue == 0) {
            tab = 'explore'
        } else if (newValue == 1) {
            tab = 'analysis'
        } else if (newValue == 2) {
            tab = 'summarize'
        } else if (newValue == 3) {
            tab = 'alternatePhrasing'
        }
        renderButtons(tab, selectedContractType)
        //if new value = 2 // alternative phrasing section
        //load alternative phrasing buttons
    }

    // jurisdiction
    const [state, setState] = React.useState('')

    const handleStateChange = event => {
        setState(event.target.value)
    }

    const exportToDocx = async () => {
        if (!superdocInstance) {
            return
        }
        try {
            await superdocInstance.export({
                triggerDownload: true,
                exportedName: 'word-export'
            })
        } catch (error) {
            console.error('DOCX export failed', error)
        }
    }

    const handleContractTypeChange = event => {
        setPendingContractType(event.target.value)
    }

    const activeEditor = getActiveEditor()
    const editorApi = getEditorApi()

    return (
        <Container
            sx={{
                px: { xs: 1.5, sm: 3 },
                pt: { xs: 1.5, sm: 4 },
                pb: { xs: 1.5, sm: '4em' }
            }}
        >
            <Box
                className="app-header-bar"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    gap: '12px',
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    '& .MuiButton-root': {
                        flex: { xs: '1 1 0', sm: '0 0 auto' },
                        minWidth: 0,
                        width: { xs: '50%', sm: 'auto' },
                        px: { xs: 1, sm: 2.75 },
                        fontSize: { xs: '0.78rem', sm: '0.9375rem' },
                        whiteSpace: 'nowrap'
                    }
                }}
            >
                <Button
                    variant="contained"
                    disableElevation
                    size="large"
                    sx={{ flex: '0 0 auto' }}
                    onClick={handleOpenUpload}
                >
                    Upload Document
                </Button>
                <Button
                    component="label"
                    variant="contained"
                    disableElevation
                    size="large"
                    sx={{ flex: '0 0 auto' }}
                    onClick={exportToDocx}
                >
                    Download Document
                </Button>
            </Box>
            {debugModelsEnabled ? (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                            p: 2,
                            border: '1px solid #dbe3ee',
                            borderRadius: '10px',
                            backgroundColor: '#f8fafc'
                        }}
                    >
                        <FormControl size="small" fullWidth>
                            <InputLabel id="identification-model-label">
                                Identify clauses model
                            </InputLabel>
                            <Select
                                labelId="identification-model-label"
                                label="Identify clauses model"
                            value={identificationModel}
                            onChange={event => {
                                setIdentificationModel(event.target.value)
                                setIdentificationEffort('default')
                            }}
                            >
                                {MODEL_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="identification-effort-label">
                            Identify clauses effort
                        </InputLabel>
                        <Select
                            labelId="identification-effort-label"
                            label="Identify clauses effort"
                            value={identificationEffort}
                            onChange={event =>
                                setIdentificationEffort(event.target.value)
                            }
                        >
                            {getModelEfforts(identificationModel).map(effort => (
                                <MenuItem key={effort} value={effort}>
                                    {EFFORT_LABELS[effort]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="analysis-model-label">
                            Analyze clauses model
                        </InputLabel>
                        <Select
                            labelId="analysis-model-label"
                            label="Analyze clauses model"
                            value={analysisModel}
                            onChange={event => {
                                setAnalysisModel(event.target.value)
                                setAnalysisEffort('default')
                            }}
                        >
                            {MODEL_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="analysis-effort-label">
                            Analyze clauses effort
                        </InputLabel>
                        <Select
                            labelId="analysis-effort-label"
                            label="Analyze clauses effort"
                            value={analysisEffort}
                            onChange={event =>
                                setAnalysisEffort(event.target.value)
                            }
                        >
                            {getModelEfforts(analysisModel).map(effort => (
                                <MenuItem key={effort} value={effort}>
                                    {EFFORT_LABELS[effort]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    </Box>
                    {identificationDurationMs != null ||
                    clauseAnalysisDurationMs != null ? (
                        <Box
                            aria-label="Analysis timing"
                            sx={{
                                display: 'flex',
                                gap: 3,
                                flexWrap: 'wrap',
                                px: 2,
                                py: 1.5,
                                borderRadius: '10px',
                                backgroundColor: '#eef4fb',
                                color: '#243447',
                                fontVariantNumeric: 'tabular-nums'
                            }}
                        >
                            <span>
                                Clause identification:{' '}
                                {identificationDurationMs == null
                                    ? 'Running'
                                    : formatDurationMs(identificationDurationMs)}
                            </span>
                            <span>
                                Clause analysis:{' '}
                                {clauseAnalysisDurationMs == null
                                    ? totalAnalysisDurationMs == null
                                        ? 'Running'
                                        : 'Not run'
                                    : formatDurationMs(clauseAnalysisDurationMs)}
                            </span>
                            {totalAnalysisDurationMs != null ? (
                                <span>
                                    Total:{' '}
                                    {formatDurationMs(totalAnalysisDurationMs)}
                                </span>
                            ) : null}
                        </Box>
                    ) : null}
                </Stack>
            ) : null}
            <input
                ref={uploadInputRef}
                type="file"
                accept=".docx"
                hidden
                onChange={handleFileUpload}
            />
            <Dialog
                fullWidth={true}
                maxWidth="md"
                open={openSettings}
                onClose={handleCloseSettings}
                disableEscapeKeyDown
                PaperProps={{
                    sx: { m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: '100%' } }
                }}
            >
                <DialogTitle>Select Document Type</DialogTitle>
                <DialogContent>
                    <br />
                    <FormControl fullWidth>
                        <InputLabel id="contract-type-label">
                            Document Type
                        </InputLabel>
                        <Select
                            labelId="contract-type-label"
                            label="Document Type"
                            value={pendingContractType}
                            onChange={handleContractTypeChange}
                        >
                            {contractTypes.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {/*
                    <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                            State
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={state}
                            label="State"
                            onChange={handleStateChange}
                        >
                            <MenuItem value={'ca'}>CA</MenuItem>
                            <MenuItem value={'other'}>Other</MenuItem>
                        </Select>
                    </FormControl>
                    */}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseSettings}
                        disabled={!pendingContractType}
                    >
                        Start
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                fullWidth
                maxWidth="md"
                open={analysisPromptDialog.open}
                onClose={() => {
                    if (!analysisPromptDialog.rerunning) {
                        setAnalysisPromptDialog(previous => ({
                            ...previous,
                            open: false
                        }))
                    }
                }}
                PaperProps={{
                    sx: { m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: '100%' } }
                }}
            >
                <DialogTitle>
                    Edit analysis Markdown: {analysisPromptDialog.clauseType}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={16}
                        margin="dense"
                        label="Clause analysis Markdown"
                        value={analysisPromptDialog.draft}
                        disabled={
                            analysisPromptDialog.loading ||
                            analysisPromptDialog.rerunning
                        }
                        onChange={event =>
                            setAnalysisPromptDialog(previous => ({
                                ...previous,
                                draft: event.target.value
                            }))
                        }
                    />
                    {analysisPromptDialog.loading ? (
                        <CircularProgress size={24} aria-label="Loading analysis Markdown" />
                    ) : null}
                    {analysisPromptDialog.error ? (
                        <Typography color="error" role="alert" sx={{ mt: 1 }}>
                            {analysisPromptDialog.error}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={analysisPromptDialog.rerunning}
                        onClick={() =>
                            setAnalysisPromptDialog(previous => ({
                                ...previous,
                                open: false
                            }))
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            analysisPromptDialog.loading ||
                            analysisPromptDialog.rerunning ||
                            !analysisPromptDialog.draft.trim()
                        }
                        onClick={rerunCommentAnalysis}
                    >
                        {analysisPromptDialog.rerunning ? (
                            <CircularProgress
                                size={18}
                                color="inherit"
                                sx={{ mr: 1 }}
                                aria-label="Re-running clause analysis"
                            />
                        ) : null}
                        {analysisPromptDialog.rerunning ? 'Re-running...' : 'Re-run'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                className="editor-toolbar"
                sx={{
                    marginBottom: '1rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    backgroundColor: '#f7f8fa',
                    padding: { xs: '6px', sm: '8px' },
                    overflow: 'hidden'
                }}
            >
                <Stack
                    role="toolbar"
                    aria-label="Document formatting"
                    useFlexGap
                    flexWrap="wrap"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{ width: '100%', minWidth: 0 }}
                >
                    <FormControl
                        size="small"
                        sx={{
                            minWidth: { xs: 132, sm: 160 },
                            width: 'auto'
                        }}
                    >
                        <Select
                            displayEmpty
                            value={
                                isActiveFormat('heading', { level: 1 })
                                    ? 'h1'
                                    : isActiveFormat('heading', { level: 2 })
                                    ? 'h2'
                                    : 'paragraph'
                            }
                            onChange={event => {
                                const value = event.target.value
                                if (!editorApi) {
                                    return
                                }
                                if (value === 'h1') {
                                    runEditorCommand('toggleHeading', { level: 1 })
                                } else if (value === 'h2') {
                                    runEditorCommand('toggleHeading', { level: 2 })
                                } else {
                                    runEditorCommand('setParagraph')
                                }
                            }}
                        >
                            <MenuItem value="paragraph">Normal text</MenuItem>
                            <MenuItem value="h1">Heading 1</MenuItem>
                            <MenuItem value="h2">Heading 2</MenuItem>
                        </Select>
                    </FormControl>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="row" spacing={0.5}>
                        <Button
                            size="small"
                            variant={isActiveFormat('bold') ? 'contained' : 'outlined'}
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleBold')
                            }}
                            disabled={!editorApi}
                            aria-label="Bold"
                            sx={{ minWidth: 36 }}
                        >
                            <FormatBoldIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            variant={isActiveFormat('italic') ? 'contained' : 'outlined'}
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleItalic')
                            }}
                            disabled={!editorApi}
                            aria-label="Italic"
                            sx={{ minWidth: 36 }}
                        >
                            <FormatItalicIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            variant={
                                isActiveFormat('underline') ? 'contained' : 'outlined'
                            }
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleUnderline')
                            }}
                            disabled={!editorApi}
                            aria-label="Underline"
                            sx={{ minWidth: 36 }}
                        >
                            <FormatUnderlinedIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            variant={isActiveFormat('strike') ? 'contained' : 'outlined'}
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleStrike')
                            }}
                            disabled={!editorApi}
                            aria-label="Strikethrough"
                            sx={{ minWidth: 36 }}
                        >
                            <StrikethroughSIcon fontSize="small" />
                        </Button>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="row" spacing={0.5}>
                        <Button
                            size="small"
                            variant={
                                isActiveFormat('bulletList') ? 'contained' : 'outlined'
                            }
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleBulletList')
                            }}
                            disabled={!editorApi}
                            aria-label="Bulleted list"
                        >
                            <FormatListBulletedIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            variant={
                                isActiveFormat('orderedList') ? 'contained' : 'outlined'
                            }
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleOrderedList')
                            }}
                            disabled={!editorApi}
                            aria-label="Numbered list"
                        >
                            <FormatListNumberedIcon fontSize="small" />
                        </Button>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack direction="row" spacing={0.5}>
                        <Button
                            size="small"
                            variant={
                                isActiveFormat('blockquote') ? 'contained' : 'outlined'
                            }
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleBlockquote')
                            }}
                            disabled={!editorApi}
                            aria-label="Block quote"
                        >
                            <FormatQuoteIcon fontSize="small" />
                        </Button>
                        <Button
                            size="small"
                            variant={
                                isActiveFormat('codeBlock') ? 'contained' : 'outlined'
                            }
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('toggleCodeBlock')
                            }}
                            disabled={!editorApi}
                            aria-label="Code block"
                        >
                            <CodeIcon fontSize="small" />
                        </Button>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            width: 'auto',
                            justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                        }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('undo')
                            }}
                            disabled={!editorApi}
                        >
                            Undo
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onMouseDown={handleToolbarMouseDown}
                            onClick={() => {
                                if (!editorApi) {
                                    return
                                }
                                runEditorCommand('redo')
                            }}
                            disabled={!editorApi}
                        >
                            Redo
                        </Button>
                    </Stack>
                </Stack>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: '16px',
                    alignItems: 'flex-start',
                    minWidth: 0
                }}
            >
                <Box
                    ref={editorCanvasRef}
                    className="editor-canvas"
                    sx={{
                        backgroundColor: '#eef1f5',
                        height: {
                            xs: '62dvh',
                            sm: 'calc(100dvh - 190px - 4em)'
                        },
                        display: 'block',
                        lineHeight: '1.6',
                        border: 0,
                        borderRadius: 0,
                        padding: 0,
                        overflowY: 'auto',
                        overflowX: { xs: 'auto', md: 'hidden' },
                        boxShadow: 'none',
                        position: 'relative',
                        flex: {
                            xs: '1 1 auto',
                            md: '0 1 auto'
                        },
                        minWidth: 0,
                        width: {
                            xs: '100%',
                            md:
                                documentViewportWidth ||
                                DEFAULT_DOCUMENT_VIEWPORT_WIDTH
                        },
                        maxWidth: '100%'
                    }}
                >
                    <Box
                        className="editor-surface"
                        sx={{
                            backgroundColor: '#ffffff',
                            maxWidth: '100%',
                            margin: '0 auto',
                            minHeight: '100%',
                            padding: 0,
                            borderRadius: '6px',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08), 0 12px 24px rgba(15, 23, 42, 0.12)',
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            overflowX: 'hidden'
                        }}
                    >
                        {documentFile ? (
                            <SuperDocEditor
                                ref={editorRef}
                                document={documentFile}
                                documentMode="editing"
                                modules={SUPERDOC_MODULES}
                                viewOptions={{
                                    layout: isMobileViewport ? 'web' : 'print'
                                }}
                                hideToolbar
                                onReady={({ superdoc }) => {
                                    setSuperdocInstance(superdoc)
                                    if (visibleEditor && superdoc?.setActiveEditor) {
                                        superdoc.setActiveEditor(visibleEditor)
                                    }
                                    setIsEditorReady(true)
                                    bumpToolbar()
                                }}
                                onEditorCreate={({ editor }) => {
                                    setVisibleEditor(editor)
                                    if (superdocInstance?.setActiveEditor) {
                                        superdocInstance.setActiveEditor(editor)
                                    }
                                }}
                                onEditorUpdate={() => bumpToolbar()}
                                renderLoading={() => (
                                    <div className="superdoc-loading">
                                        Loading document...
                                    </div>
                                )}
                                className="superdoc-editor"
                                style={{ minHeight: '60vh' }}
                            />
                        ) : (
                            <div
                                className="superdoc-placeholder"
                                role="button"
                                tabIndex={0}
                                onClick={handleOpenUpload}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        handleOpenUpload()
                                    }
                                }}
                            >
                                Upload a DOCX file to begin.
                            </div>
                        )}
                    </Box>
                </Box>
                <Box className="comment-panel">
                    <div className="comment-panel__header">
                        Analysis Comments
                    </div>
                    {comments.length ? (
                        isMobileViewport ? (
                            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                <InputLabel id="mobile-analysis-clause-label">
                                    Clause analysis
                                </InputLabel>
                                <Select
                                    labelId="mobile-analysis-clause-label"
                                    label="Clause analysis"
                                    value={expandedCommentId || ''}
                                    onChange={event => {
                                        const selectedComment = comments.find(
                                            comment => comment.id === event.target.value
                                        )
                                        if (selectedComment) {
                                            focusComment(selectedComment)
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        Select a clause
                                    </MenuItem>
                                    {comments.map(comment => (
                                        <MenuItem key={comment.id} value={comment.id}>
                                            {comment.clauseType}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null
                    ) : null}
                    {comments.length ? (
                        comments.map(comment => {
                            const isExpanded = expandedCommentId === comment.id
                            return (
                                <div
                                    key={comment.id}
                                    data-outcome={comment.outcome}
                                    className={`comment-panel__item ${
                                        isExpanded ? 'is-expanded' : ''
                                    }`}
                                    style={{
                                        '--finding-accent': comment.baseColor,
                                        '--finding-background': comment.panelColor,
                                        '--finding-active-background':
                                            comment.panelActiveColor,
                                        display:
                                            isMobileViewport && !isExpanded
                                                ? 'none'
                                                : 'block'
                                    }}
                                >
                                    <button
                                        className="comment-panel__toggle"
                                        onClick={() => focusComment(comment)}
                                        type="button"
                                        aria-expanded={isExpanded}
                                    >
                                        <div className="comment-panel__title">
                                            {comment.clauseType}
                                        </div>
                                        {isExpanded ? (
                                            <div
                                                className="comment-panel__body"
                                                dangerouslySetInnerHTML={{
                                                    __html: comment.analysisHtml
                                                }}
                                            />
                                        ) : null}
                                    </button>
                                    {isExpanded && debugModelsEnabled ? (
                                        <Button
                                            size="small"
                                            sx={{ mt: 1 }}
                                            onClick={() =>
                                                openAnalysisPromptDialog(comment)
                                            }
                                        >
                                            Edit analysis Markdown
                                        </Button>
                                    ) : null}
                                </div>
                            )
                        })
                    ) : (
                        <div className="comment-panel__empty">
                            No analysis comments yet.
                        </div>
                    )}
                </Box>
            </Box>
            {hasTabResult && analysis ? (
                <div
                    style={{
                        borderColor: 'lightBlue',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        marginTop: '1.5em',
                        marginBottom: '1.5em',
                        padding: '1em',
                        color: '#0096FF',
                        fontWeight: 600,
                        backgroundColor: 'white'
                    }}
                >
                    <div
                        style={{
                            fontSize: '1.5em',
                            marginBottom: '1em'
                        }}
                    >
                        Analysis
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: analysis }} />
                </div>
            ) : (
                ''
            )}
            {selectedContractType &&
            selectedContractType !== VIRGINIA_PRIVACY_LAW ? (
                <div hidden>
                    <Box sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            borderBottom: '1px solid #d5dbe4',
                            backgroundColor: '#f2f5f9',
                            borderRadius: '10px',
                            padding: '16px 6px 6px',
                            boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)'
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="Action Tab"
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                minHeight: '40px',
                                '& .MuiTabs-indicator': {
                                    height: 0
                                },
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    minHeight: '32px',
                                    padding: '4px 14px',
                                    borderRadius: '8px',
                                    margin: '0 4px',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center'
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    backgroundColor: '#1e88e5',
                                    color: '#ffffff',
                                    boxShadow:
                                        '0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.08)'
                                },
                                '& .MuiTabs-flexContainer': {
                                    justifyContent: 'flex-start',
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {supportsExplore(selectedContractType) ? (
                                <Tab
                                    value={0}
                                    label="Explore Topic"
                                    {...a11yProps(0)}
                                />
                            ) : null}
                            <Tab value={1} label="Analysis" {...a11yProps(1)} />
                            <Tab
                                value={2}
                                label="Summarization"
                                {...a11yProps(2)}
                            />
                            <Tab
                                value={3}
                                label="Alternate Phrasing"
                                {...a11yProps(3)}
                            />
                        </Tabs>
                    </Box>
                    {supportsExplore(selectedContractType) ? (
                        <TabPanel value={tabValue} index={0}>
                            <Stack
                                useFlexGap
                                flexWrap="wrap"
                                direction="row"
                                spacing={2}
                                sx={{
                                    width: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: '1fr 1fr'
                                    },
                                    gap: 2
                                }}
                            >
                                {buttons.map(topic => (
                                    <Button
                                        key={topic.name}
                                        disableElevation
                                        style={{
                                            width: '100%'
                                        }}
                                        variant="contained"
                                        onClick={() => exploreTopic(topic)}
                                        disabled={isLoading ? true : false}
                                        color="info"
                                    >
                                        {topic.name}
                                    </Button>
                                ))}
                            </Stack>
                        </TabPanel>
                    ) : null}
                    <TabPanel value={tabValue} index={1}>
                        <Stack
                            useFlexGap
                            flexWrap="wrap"
                            direction="row"
                            spacing={2}
                            sx={{
                                width: '100%',
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr'
                                },
                                gap: 2
                            }}
                        >
                            {buttons.map(clause => (
                                <Button
                                    key={clause.name}
                                    disableElevation
                                    style={{
                                        width: '100%'
                                    }}
                                    variant="contained"
                                    onClick={() =>
                                        analyze(clause.type, selectedContractType)
                                    }
                                    disabled={isLoading ? true : false}
                                >
                                    {clause.name}
                                </Button>
                            ))}
                        </Stack>
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                        <Stack
                            spacing={2}
                            useFlexGap
                            flexWrap="wrap"
                            direction="row"
                            sx={{
                                width: '100%',
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr'
                                },
                                gap: 2
                            }}
                        >
                            {buttons.map(clause => (
                                <Button
                                    key={clause.name}
                                    disableElevation
                                    style={{
                                        width: '100%'
                                    }}
                                    variant="contained"
                                    onClick={() => summarize(clause.type)}
                                    disabled={isLoading ? true : false}
                                    color="secondary"
                                >
                                    {clause.name}
                                </Button>
                            ))}
                        </Stack>
                    </TabPanel>
                    <TabPanel value={tabValue} index={3}>
                        <Stack
                            useFlexGap
                            flexWrap="wrap"
                            direction="row"
                            spacing={2}
                            sx={{
                                width: '100%',
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr'
                                },
                                gap: 2
                            }}
                        >
                            {buttons.map(clause => (
                                <Button
                                    key={clause.name}
                                    disableElevation
                                    style={{
                                        width: '100%'
                                    }}
                                    variant="contained"
                                    onClick={() => paraphrase(clause.type)}
                                    disabled={isLoading ? true : false}
                                    color="warning"
                                >
                                    {clause.name}
                                </Button>
                            ))}
                        </Stack>
                    </TabPanel>
                </Box>
                </div>
            ) : null}
            {isLoading ? (
                <div className="analysis-overlay" role="status" aria-live="polite">
                    <div className="analysis-overlay__card">
                        <CircularProgress color="primary" />
                        <div className="analysis-overlay__text">
                            Running contract analysis...
                            {debugModelsEnabled && analysisStartedAt != null
                                ? ` ${formatElapsedTime(analysisElapsedSeconds)}`
                                : null}
                        </div>
                    </div>
                </div>
            ) : (
                ''
            )}
            <ParaphraseModal
                open={paraphraseModalOpen}
                originalClause={clause}
                steps={steps}
                newGeneratedClause={newFavClause}
                isLoading={isLoading}
                onClose={tmpTxt => handleUpdatedClause(tmpTxt)}
                onClauseFavorabilityChange={handleOnClauseFavorabilityChange}
            />
        </Container>
    )
}

export default App
